import { Injectable } from '@angular/core'
import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as me from '../../me'

import * as gql from './gql'
import type { Entity, TypeParams } from './types'

/** @see {@link ec.models.Api} */
@Injectable({ providedIn: 'root' })
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(me.Feature)
  /** {@link ec.Collection Collection} of {@link Entity entities} */
  public readonly processesEc$ = ec.Collection.instances.get<TypeParams>(
    ec.Name.Predefined.RelayProcess,
  ).asObservable

  /**
   * Retrieves a list of processes for the given context, including processes at different levels:
   * - Zone-level processes
   * - App-level processes for each application the user has access to in the current zone
   * - Scenario-level processes for each scenario in each application
   */
  // NOTE: This implementation is more of a workaround than the intended way to
  // use GraphQL. Currently, we're dealing with an O(n) problem, meaning that as
  // the number of zones, apps, and scenarios increases, the number of requests
  // to our GraphQL server will grow exponentially, potentially overwhelming it.
  // The intended approach would be to implement "second-level" resolvers on the
  // back-end, which would significantly reduce the number of requests sent to
  // the server. However, this approach is complex and may not be feasible in
  // the short term.

  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context?.isOpen) {
        return rxjs.of([])
      }

      return this.meFeature.zoneApps.selectors
        .getResult(context.zoneId)
        .asObservable.pipe(
          rxjs.filter(({ isSettled }) => isSettled),
          rxjs.take(1),
          rxjs.switchMap(({ apps, error }) => {
            if (error) {
              return rxjs.throwError(() => error)
            }
            return rxjs
              .forkJoin([
                _fetchZoneProcesses(context),
                ...(apps ?? []).flatMap(app => [
                  _fetchAppProcesses({ ...context, appId: app.id }),
                  ...app.scenarios.map(scenario =>
                    _fetchScenarioProcesses({
                      ...context,
                      appId: app.id,
                      scenarioId: scenario.id,
                    }),
                  ),
                ]),
              ])
              .pipe(rxjs.map(results => results.flat()))
          }),
        )
    },
  )

  /**
   * Deletes/dismisses the desired processes
   *
   * Throws an error if no context is provided
   */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)

    return this.processesEc$.pipe(
      rxjs.switchMap(processesEc =>
        // NOTE: We retrieve the full process entity from cache to extract appId and scenarioId.
        // The processEc context is intentionally scoped to only zoneId/tenantId to aggregate
        // processes across all apps and scenarios, but we need the specific app/scenario IDs
        // to target the correct process for dismissal via the GraphQL mutation.
        processesEc.plugins.cachePlugin.feature.selectors
          .getCachedEntity(id)
          // NOTE: We only need the current snapshot of the process entity; we
          // don't need to observe changes
          .asObservable.pipe(rxjs.take(1)),
      ),
      rxjs.switchMap(processEssential =>
        gql.mutations.dismissProcess.executor({
          process: {
            appId: processEssential?.appId,
            id,
            scenarioId: processEssential?.scenarioId,
            tenantId: context.tenantId,
            zoneId: context.zoneId,
          },
        }),
      ),
    )
  }

  /**
   * Stops a running process by terminating its ECS task
   *
   * Throws an error if no context is provided
   */
  public stop(
    context: ents.core.admin.Context.ForZone,
    id: string,
  ): rxjs.Observable<void> {
    TypeAsserter.assertIsNotNullish(context)

    return this.processesEc$.pipe(
      rxjs.switchMap(processesEc =>
        processesEc.plugins.cachePlugin.feature.selectors
          .getCachedEntity(id)
          .asObservable.pipe(rxjs.take(1)),
      ),
      rxjs.switchMap(processEssential =>
        gql.mutations.stopProcess.executor({
          process: {
            appId: processEssential?.appId,
            id,
            scenarioId: processEssential?.scenarioId,
            tenantId: context.tenantId,
            zoneId: context.zoneId,
          },
        }),
      ),
    )
  }
}

/**
 * Fetches the processes associated with the given zone.
 */
function _fetchZoneProcesses(context: ents.core.admin.Context.ForZone) {
  return gql.queries.zoneProcesses.executor({
    tenantId: context.tenantId,
    zoneId: context.zoneId,
  })
}

/**
 * Fetches the processes associated with the specified application in the given zone.
 */
function _fetchAppProcesses(context: ents.core.admin.Context.ForApp) {
  return gql.queries.appProcesses.executor({
    appId: context.appId,
    tenantId: context.tenantId,
    zoneId: context.zoneId,
  })
}

/**
 * Fetches the processes associated with the specified scenario within an application.
 */
function _fetchScenarioProcesses(context: ents.core.admin.Context.ForScenario) {
  return gql.queries.scenarioProcesses.executor({
    appId: context.appId,
    scenarioId: context.scenarioId,
    tenantId: context.tenantId,
    zoneId: context.zoneId,
  })
}
